<template>
  <div class="slider-captcha" :style="{ width: `${width}px` }">
    <!-- 验证码主区域 -->
    <div class="captcha-area">
      <!-- 背景图区域 -->
      <div class="background-container" :style="{ height: `${height}px` }">
        <img :src="bgImageUrl" class="background-image" ref="bgImage" @load="onBgLoaded" @error="onBgError" />

        <!-- 缺口高亮边框（可选，调试用） -->
        <div v-if="showGapHighlight && targetX" class="gap-highlight"
             :style="{ left: `${targetX}px`, top: `${gapY}px`, width: `${sliderWidth}px`, height: `${sliderHeight}px` }">
          <div class="gap-border"></div>
        </div>

        <!-- 滑块遮罩 -->
        <div class="slider-mask"
             :style="{ left: `${sliderLeft}px`, top: `${gapY}px`, width: `${sliderWidth}px`, height: `${sliderHeight}px` }">
          <img :src="sliderImageUrl" class="slider-image" />
          <div v-if="isDragging" class="offset-tip">{{ offsetTip }}</div>
        </div>

        <!-- 加载遮罩 -->
        <div v-if="loading" class="loading-overlay">
          <div class="loading-spinner"></div>
          <span>加载中…</span>
        </div>
      </div>

      <!-- 滑块轨道（含刷新按钮） -->
      <div class="slider-track" ref="track">
        <!-- 滑块按钮 -->
        <div class="slider-button"
             :style="{ left: `${sliderLeft}px` }"
             :class="{ dragging: isDragging }"
             @mousedown.prevent="onMouseDown"
             @touchstart.prevent="onTouchStart">
          <span class="slider-icon">⇨</span>
        </div>

        <!-- 轨道文字提示 -->
        <span class="track-text">{{ dragText }}</span>

        <!-- 刷新按钮（圆形图标） -->
        <button class="refresh-button"
                @click="refresh"
                :disabled="refreshing || loading || verified"
                :title="'刷新验证码'">
          <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2" fill="none">
            <path d="M23 4v6h-6M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </button>
      </div>
    </div>

    <!-- 状态提示区（仍保留作为辅助） -->
    <!-- <div class="status-area" :class="{ error: showError, success: verified }">
      {{ statusMessage }}
    </div> -->
  </div>
</template>

<script>

const throttle = (func, limit) => {
  let inThrottle
  return function() {
    const args = arguments
    const context = this
    if (!inThrottle) {
      func.apply(context, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

export default {
  name: 'SliderCaptcha',
  props: {
    width: { type: Number, default: 400 },
    height: { type: Number, default: 200 },
    sliderWidth: { type: Number, default: 60 },
    sliderHeight: { type: Number, default: 60 },
    apiUrl: { type: String, default: '/api/Captcha' },
    deviceId: { type: String, required: true },
    showGapHighlight: { type: Boolean, default: false }
  },
  data() {
    return {
      bgImage: '',
      sliderImage: '',
      gapY: 0,
      targetX: 0,
      captchaToken: '',
      timestamp: 0,
      sign: '',
      loading: true,
      refreshing: false,
      isDragging: false,
      verified: false,
      showError: false,
      sliderLeft: 0,
      rafId: null,
      targetSliderLeft: 0,
      trackPoints: [],
      dragStartTime: 0,
      startX: 0,
      startLeft: 0,
      dragText: '拖动滑块使图片重合',
      statusMessage: '请完成安全验证',
      mouseMoveHandler: null,
      mouseUpHandler: null,
      touchMoveHandler: null,
      touchEndHandler: null,
      naturalWidth: 0,
      naturalHeight: 0
    }
  },
  computed: {
    bgImageUrl() { return this.bgImage ? `data:image/png;base64,${this.bgImage}` : '' },
    sliderImageUrl() { return this.sliderImage ? `data:image/png;base64,${this.sliderImage}` : '' },
    maxSliderLeft() { return this.width - this.sliderWidth },
    offsetTip() {
      if (!this.isDragging) return ''
      const diff = this.sliderLeft - this.targetX
      if (Math.abs(diff) <= 5) return '✓ 位置正确'
      return diff > 0 ? `向右偏移 ${diff}px` : `向左偏移 ${Math.abs(diff)}px`
    }
  },
  mounted() {
    this.init()
    this.setupListeners()
  },
  beforeDestroy() {
    this.removeListeners()
    if (this.rafId) cancelAnimationFrame(this.rafId)
  },
  methods: {
    async init() {
      this.loading = true
      try {
		  debugger
        const res = await this.$axios.post(`${this.apiUrl}/Generate`, { deviceId: this.deviceId })
        if (res.data.success) {
          this.bgImage = res.data.bgImage
          this.sliderImage = res.data.sliderImage
          this.gapY = res.data.gapY
          this.targetX = res.data.targetX
          this.captchaToken = res.data.token
          this.timestamp = res.data.timestamp
          this.sign = res.data.sign
          this.reset()
          this.statusMessage = '拖动滑块使图片重合'
        } else {
          throw new Error(res.data.message || '生成验证码失败')
        }
      } catch (err) {
        this.statusMessage = '加载失败，请刷新'
        this.showError = true
        this.$Notice.error({
          title: '验证码加载失败',
          desc: err.message || '请检查网络后重试'
        })
      } finally {
        this.loading = false
        this.refreshing = false
      }
    },
    refresh() {
      if (this.refreshing || this.loading || this.verified) return
      this.refreshing = true
      this.init()
    },
    reset() {
      this.sliderLeft = 0
      this.targetSliderLeft = 0
      this.trackPoints = []
      this.dragStartTime = 0
      this.verified = false
      this.isDragging = false
      this.showError = false
      this.dragText = '拖动滑块使图片重合'
    },

    // 拖动逻辑
    startDrag(clientX, pageY) {
      if (this.verified || this.loading || this.refreshing) return
      this.isDragging = true
      this.startX = clientX
      this.startLeft = this.sliderLeft
      this.dragStartTime = Date.now()
      this.trackPoints = [{ x: this.sliderLeft, y: pageY, t: this.dragStartTime }]
      this.dragText = '拖动中...'
      this.bindGlobalEvents()
    },
    onMouseDown(e) {
      this.startDrag(e.clientX, e.pageY)
    },
    onTouchStart(e) {
      e.preventDefault()
      const touch = e.touches[0]
      this.startDrag(touch.clientX, touch.pageY)
    },
    updateDrag(clientX, pageY) {
      let newLeft = this.startLeft + (clientX - this.startX)
      newLeft = Math.max(0, Math.min(newLeft, this.maxSliderLeft))
      this.targetSliderLeft = newLeft
      if (!this.rafId) {
        this.rafId = requestAnimationFrame(this.applySliderPosition)
      }
      const now = Date.now()
	  let trackPoint = this.trackPoints[this.trackPoints.length - 1]
      if (now - (trackPoint?trackPoint.t:0 || 0) > 16) {
        this.trackPoints.push({ x: newLeft, y: pageY, t: now })
        if (this.trackPoints.length > 100) this.trackPoints = this.trackPoints.slice(-80)
      }
    },
    applySliderPosition() {
      this.sliderLeft = this.targetSliderLeft
      this.rafId = null
    },
    onMouseMove: throttle(function(e) {
      if (!this.isDragging) return
      this.updateDrag(e.clientX, e.pageY)
    }, 8),
    onTouchMove: throttle(function(e) {
      if (!this.isDragging) return
      e.preventDefault()
      const touch = e.touches[0]
      this.updateDrag(touch.clientX, touch.pageY)
    }, 8),
    async endDrag() {
      this.isDragging = false
      this.unbindGlobalEvents()
      if (this.rafId) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
        this.sliderLeft = this.targetSliderLeft
      }
      if (this.trackPoints.length < 5) {
        this.showErrorMsg('拖动距离太短')
        this.$Notice.warning({
          title: '验证失败',
          desc: '拖动距离太短，请重新拖动'
        })
        this.animateBack()
        return
      }
      await this.verify()
    },
    onMouseUp() { this.endDrag() },
    onTouchEnd() { this.endDrag() },

    async verify() {
      this.statusMessage = '验证中...'
      try {
        const res = await this.$axios.post(`${this.apiUrl}/Verify`, {
          captchaToken: this.captchaToken,
          moveX: this.sliderLeft,
          track: this.trackPoints,
          timestamp: this.timestamp,
          sign: this.sign,
          deviceId: this.deviceId
        })
        if (res.data.state) {
          this.verified = true
          this.statusMessage = '验证通过'
          this.dragText = '✓ 验证成功'
          this.$Notice.success({
            title: '验证成功',
            desc: '安全验证已通过'
          })
          this.$emit('verified', { sessionToken: res.data.data.sessionToken, expiresAt: res.data.data.expiresAt })
        } else {
          this.showErrorMsg(res.data.errorMsg || '验证失败')
          this.$Notice.error({
            title: '验证失败',
            desc: res.data.errorMsg || '请重新尝试'
          })
          this.animateBack()
        }
      } catch (err) {
        this.showErrorMsg('网络错误，请重试')
        this.$Notice.error({
          title: '网络错误',
          desc: '验证请求失败，请检查网络'
        })
        this.animateBack()
      }
    },

    showErrorMsg(msg) {
      this.statusMessage = msg
      this.showError = true
      setTimeout(() => {
        if (!this.verified) {
          this.statusMessage = '请完成安全验证'
          this.showError = false
        }
      }, 2500)
    },
    animateBack() {
      let left = this.sliderLeft
      const step = () => {
        left -= 8
        if (left > 0) {
          this.sliderLeft = left
          this.targetSliderLeft = left
          requestAnimationFrame(step)
        } else {
          this.sliderLeft = 0
          this.targetSliderLeft = 0
          this.trackPoints = []
        }
      }
      step()
    },

    // 事件监听
    setupListeners() {
      this.mouseMoveHandler = (e) => this.onMouseMove(e)
      this.mouseUpHandler = () => this.onMouseUp()
      this.touchMoveHandler = (e) => this.onTouchMove(e)
      this.touchEndHandler = () => this.onTouchEnd()
    },
    bindGlobalEvents() {
      document.addEventListener('mousemove', this.mouseMoveHandler)
      document.addEventListener('mouseup', this.mouseUpHandler)
      document.addEventListener('touchmove', this.touchMoveHandler, { passive: false })
      document.addEventListener('touchend', this.touchEndHandler)
    },
    unbindGlobalEvents() {
      document.removeEventListener('mousemove', this.mouseMoveHandler)
      document.removeEventListener('mouseup', this.mouseUpHandler)
      document.removeEventListener('touchmove', this.touchMoveHandler)
      document.removeEventListener('touchend', this.touchEndHandler)
    },
    removeListeners() {
      this.unbindGlobalEvents()
    },

    onBgLoaded() {
      const img = this.$refs.bgImage
      if (img) {
        this.naturalWidth = img.naturalWidth
        this.naturalHeight = img.naturalHeight
      }
    },
    // ✅ 修复：增加条件判断，避免空图片触发错误提示
    onBgError() {
      // 仅在 bgImage 有值且当前正在加载时提示错误（避免初始无图时的误报）
      if (this.bgImage && this.loading) {
        this.statusMessage = '背景图加载失败'
        this.showError = true
        this.$Notice.error({
          title: '图片加载失败',
          desc: '背景图加载异常，请刷新重试'
        })
      }
    }
  }
}
</script>

<style scoped>
.slider-captcha {
  user-select: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  margin-bottom: 16px;
}

.captcha-area {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  background: #fff;
}

.background-container {
  position: relative;
  background: #f8f9fa;
}

.background-image {
  width: 100%;
  height: 100%;
  display: block;
}

/* 缺口高亮（调试用） */
.gap-highlight {
  position: absolute;
  pointer-events: none;
  z-index: 1;
}
.gap-border {
  width: 100%;
  height: 100%;
  border: 2px solid rgba(24,144,255,0.8);
  border-radius: 6px;
  box-shadow: 0 0 0 2px rgba(24,144,255,0.2);
  animation: pulse 1.5s infinite;
}
@keyframes pulse {
  0% { border-color: rgba(24,144,255,0.8); }
  50% { border-color: rgba(24,144,255,0.4); }
  100% { border-color: rgba(24,144,255,0.8); }
}

/* 滑块遮罩 */
.slider-mask {
  position: absolute;
  top: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 3;
  border-radius: 6px;
  box-shadow: 0 0 0 1px rgba(255,255,255,0.8) inset, 0 2px 8px rgba(0,0,0,0.15);
}
.slider-image {
  width: 100%;
  height: 100%;
  display: block;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
}

/* 实时偏移提示 */
.offset-tip {
  position: absolute;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.7);
  color: white;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  white-space: nowrap;
  pointer-events: none;
}

/* 加载遮罩 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255,255,255,0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.loading-spinner {
  width: 30px;
  height: 30px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 滑块轨道 */
.slider-track {
  position: relative;
  height: 50px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  display: flex;
  align-items: center;
}

/* 滑块按钮 */
.slider-button {
  position: absolute;
  top: 0;
  left: 0;
  width: 60px;
  height: 50px;
  background: linear-gradient(180deg, #fff 0%, #f8f9fa 100%);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  transition: background 0.2s;
  z-index: 4;
}
.slider-button.dragging {
  cursor: grabbing;
  background: linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%);
  box-shadow: 0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.1);
}
.slider-icon {
  color: #495057;
  font-size: 20px;
  font-weight: bold;
}

/* 轨道文字 */
.track-text {
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
  font-weight: 500;
  pointer-events: none;
  z-index: 2;
}

/* 美化后的刷新按钮：圆形，悬浮在右侧 */
.refresh-button {
  position: absolute;
  right: 8px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e0e0e0;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 5;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  padding: 0;
  outline: none;
}

.refresh-button:hover:not(:disabled) {
  background: #fff;
  color: #1890ff;
  border-color: #1890ff;
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.refresh-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refresh-button svg {
  stroke: currentColor;
}

/* 状态提示区（辅助显示） */
.status-area {
  margin-top: 8px;
  font-size: 12px;
  color: #6c757d;
  text-align: center;
  min-height: 20px;
  transition: color 0.3s;
}
.status-area.error {
  color: #f5222d;
}
.status-area.success {
  color: #52c41a;
}
</style>